import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Heartbeat, Car, CircleNotch, MagnifyingGlass, ShieldWarning, Warning } from "@phosphor-icons/react";
import { DatePickerID } from "../components/DatePickerID";
import { MissingDocsBox, FilesBox } from "../components/claim/ClaimFormSections";

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}{required && <span className="text-red-600 ml-0.5">*</span>}
    </Label>
    {children}
  </div>
);

const namesMatch = (a, b) => {
  const na = (a || "").trim().toLowerCase();
  const nb = (b || "").trim().toLowerCase();
  if (!na || !nb) return true;
  if (na === nb) return true;
  if (/\bqq\b/.test(nb)) return nb.split(/\bqq\b/).map((p) => p.replace(/[.,]+$/, "").trim()).filter(Boolean).some((p) => p === na);
  return false;
};

const COMMERCIAL_RE = /\bsewa\b|\bdisewa\w*|\bmenyewak?an\b|\bpenyewa\b|\brental\b|\bdirental\w*|\bojek\b|\bojol\b|\bgojek\b|\bgrab\w*|\bmaxim\b|\btaksi\b|\btaxi\b|\bangkutan umum\b|\bangkut penumpang\b|\bmengangkut penumpang\b|\bcarter\b|\bdicarter\b|\bekspedisi\b|\bkurir\b|\bantar barang\b|\bangkut barang\b/i;

const initCommon = {
  policy_number: "", policy_start_date: "", policy_end_date: "", incident_date: "", claim_amount: "",
  province: "", city: "", district: "", chronology: "", documents_complete: true,
  previous_claims_count: 0, reporter_name: "",
};

export default function ClaimFormPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("jiwa");
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [common, setCommon] = useState(initCommon);
  const [jiwa, setJiwa] = useState({ insured_name: "", insured_age: "", cause_of_death: "", beneficiary_relation: "" });
  const [kendaraan, setKendaraan] = useState({
    insured_name: "", vehicle_brand: "", plate_number: "", vehicle_year: "", vehicle_value: "",
    coverage_type: "", usage_type: "", incident_type: "", police_report: true, incident_hour: "", witness_present: true,
  });
  const [hadPrevious, setHadPrevious] = useState(false);
  const [missingDocs, setMissingDocs] = useState([]);
  const [otherDoc, setOtherDoc] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [files, setFiles] = useState([]);
  const [policyInfo, setPolicyInfo] = useState(null);
  const [policyStatus, setPolicyStatus] = useState(null); // null | found | not_found

  useEffect(() => { api.get("/regions").then((r) => setRegions(r.data)).catch((err) => console.error("Gagal memuat wilayah:", err)); }, []);

  const setC = (k, v) => setCommon((s) => ({ ...s, [k]: v }));
  const setK = (k, v) => setKendaraan((s) => ({ ...s, [k]: v }));

  const lookupPolicy = async () => {
    if (!common.policy_number.trim()) return;
    setLookingUp(true);
    try {
      const { data } = await api.get(`/policies/lookup/${encodeURIComponent(common.policy_number.trim())}`);
      setPolicyInfo({ policy_number: data.policy_number, holder_name: data.holder_name });
      setPolicyStatus("found");
      setTab(data.claim_type);
      setC("policy_start_date", data.policy_start_date || "");
      setC("policy_end_date", data.policy_end_date || "");
      if (data.claim_type === "jiwa") {
        setJiwa((s) => ({ ...s, insured_name: data.insured_name || "", insured_age: data.insured_age ?? "" }));
      } else {
        setKendaraan((s) => ({ ...s, insured_name: data.insured_name || data.holder_name || "",
          vehicle_brand: data.vehicle_brand || "", plate_number: data.plate_number || "",
          vehicle_year: data.vehicle_year ?? "", vehicle_value: data.vehicle_value ?? "",
          coverage_type: data.coverage_type || "", usage_type: data.usage_type || "" }));
      }
      toast.success(`Polis ${data.policy_number} ditemukan — data polis terisi otomatis`);
    } catch {
      setPolicyInfo(null);
      setPolicyStatus("not_found");
      toast.info("Polis tidak ditemukan di master. Isi data polis secara manual.");
    } finally {
      setLookingUp(false);
    }
  };

  const commercialWarning = tab === "kendaraan" && kendaraan.usage_type === "pribadi_dinas" && COMMERCIAL_RE.test(common.chronology);

  const submit = async () => {
    setLoading(true);
    try {
      const missing = common.documents_complete ? [] : [...missingDocs, ...(otherDoc.trim() ? [otherDoc.trim()] : [])];
      const base = { ...common, claim_type: tab, claim_amount: Number(common.claim_amount),
        previous_claims_count: tab === "kendaraan" ? (hadPrevious ? Number(common.previous_claims_count) || 0 : 0) : Number(common.previous_claims_count) || 0,
        policy_end_date: common.policy_end_date || null, missing_documents: missing };
      const payload = tab === "jiwa"
        ? { ...base, ...jiwa, insured_age: jiwa.insured_age ? Number(jiwa.insured_age) : null }
        : { ...base, ...kendaraan,
            coverage_type: kendaraan.coverage_type || null, usage_type: kendaraan.usage_type || null,
            vehicle_year: kendaraan.vehicle_year ? Number(kendaraan.vehicle_year) : null,
            vehicle_value: Number(kendaraan.vehicle_value),
            incident_hour: kendaraan.incident_hour !== "" ? Number(kendaraan.incident_hour) : null };
      const { data } = await api.post("/claims", payload);
      let uploaded = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try { await api.post(`/claims/${data.id}/documents`, fd); uploaded++; }
        catch (err) { console.error(`Unggah dokumen "${f.name}" gagal:`, err); }
      }
      toast.success(`Klaim ${data.claim_number} tersimpan — skor ${data.score} (${data.risk_category})${files.length ? ` · ${uploaded}/${files.length} dokumen terunggah` : ""}`);
      navigate("/klaim");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const policyNumberField = (
    <Field label="Nomor Polis" required>
      <div className="flex gap-2">
        <Input data-testid="input-policy-number" value={common.policy_number} onChange={(e) => setC("policy_number", e.target.value)}
          onBlur={lookupPolicy} placeholder="POL-2024-XXXX" />
        <Button type="button" data-testid="policy-lookup-button" variant="outline" disabled={lookingUp}
          onClick={lookupPolicy} className="rounded-none shrink-0 px-3" title="Cari di master polis">
          {lookingUp ? <CircleNotch size={15} className="animate-spin" /> : <MagnifyingGlass size={15} />}
        </Button>
      </div>
      {policyStatus === "not_found" && (
        <p data-testid="policy-not-found-badge" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">
          Polis tidak terdaftar di master — isi data polis secara manual
        </p>
      )}
      {policyStatus === "found" && (
        <p data-testid="policy-found-badge" className="text-xs text-emerald-700">Polis ditemukan di master data</p>
      )}
    </Field>
  );

  const reporterField = (
    <Field label="Nama Pelapor" required>
      <Input data-testid="input-reporter-name" value={common.reporter_name} onChange={(e) => setC("reporter_name", e.target.value)} placeholder="Nama lengkap pelapor" />
      {policyInfo?.holder_name && common.reporter_name.trim() && (
        !namesMatch(common.reporter_name, policyInfo.holder_name) ? (
          <p data-testid="holder-mismatch-warning" className="text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1.5">
            Pelapor berbeda dengan pemegang polis "{policyInfo.holder_name}" — indikasi pinjam nama / oper alih (Pasal 27 ayat 4 PSAKBI)
          </p>
        ) : (
          <p data-testid="holder-match-ok" className="text-xs text-emerald-700">Pelapor sesuai pemegang polis di master data</p>
        )
      )}
    </Field>
  );

  const chronologyField = (
    <Field label="Kronologi Kejadian" required>
      <Textarea data-testid="input-chronology" rows={5} value={common.chronology} onChange={(e) => setC("chronology", e.target.value)}
        placeholder="Jelaskan kronologi kejadian secara detail. Teks ini akan dianalisis otomatis oleh sistem." />
      <p className="text-xs text-muted-foreground">Kronologi dianalisis dengan phrase dictionary untuk mendeteksi indikasi mencurigakan.</p>
      {commercialWarning && (
        <div data-testid="commercial-use-warning" className="flex gap-2 text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-2">
          <ShieldWarning size={16} className="shrink-0 mt-0.5" />
          <p><b>Indikasi penggunaan komersial:</b> kronologi mengindikasikan kendaraan disewakan/digunakan komersial padahal polis tercatat penggunaan Pribadi/Dinas — potensi pelanggaran <b>Pasal 8 PSAKBI</b> (perubahan risiko wajib diberitahukan maks. 7 hari). Klaim akan diberi poin risiko tambahan.</p>
        </div>
      )}
    </Field>
  );

  const filesSection = <FilesBox files={files} setFiles={setFiles} />;

  const missingDocsSection = (
    <MissingDocsBox visible={!common.documents_complete} missingDocs={missingDocs} setMissingDocs={setMissingDocs}
      otherDoc={otherDoc} setOtherDoc={setOtherDoc} />
  );

  const disabled = !common.policy_number || !common.policy_start_date || !common.incident_date ||
    !common.claim_amount || !common.province || !common.chronology || !common.reporter_name ||
    (tab === "jiwa" ? !jiwa.insured_name || !jiwa.cause_of_death : !kendaraan.plate_number || !kendaraan.vehicle_value || !kendaraan.insured_name);

  return (
    <div className="p-8 max-w-4xl" data-testid="claim-form-page">
      <h1 className="text-2xl font-semibold tracking-tight">Input Klaim Baru</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">Klaim akan diskor otomatis oleh rules engine setelah disimpan</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md rounded-none h-11">
          <TabsTrigger data-testid="tab-jiwa" value="jiwa" className="rounded-none gap-2"><Heartbeat size={16} /> Asuransi Jiwa</TabsTrigger>
          <TabsTrigger data-testid="tab-kendaraan" value="kendaraan" className="rounded-none gap-2"><Car size={16} /> Asuransi Kendaraan</TabsTrigger>
        </TabsList>

        {/* ===================== JIWA ===================== */}
        <TabsContent value="jiwa" className="mt-4 space-y-4">
          <div className="bg-card border border-border p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {policyNumberField}
              <Field label="Tanggal Mulai Polis" required>
                <DatePickerID testId="input-policy-start-date" value={common.policy_start_date} onChange={(v) => setC("policy_start_date", v)} />
              </Field>
              <Field label="Tanggal Kejadian" required>
                <DatePickerID testId="input-incident-date" value={common.incident_date} onChange={(v) => setC("incident_date", v)}
                  disabled={{ after: new Date() }} />
              </Field>
              <Field label="Nilai Klaim (Rp)" required>
                <Input data-testid="input-claim-amount" type="number" min="0" value={common.claim_amount} onChange={(e) => setC("claim_amount", e.target.value)} placeholder="250000000" />
              </Field>
              <Field label="Provinsi" required>
                <Select value={common.province} onValueChange={(v) => setC("province", v)}>
                  <SelectTrigger data-testid="select-province"><SelectValue placeholder="Pilih provinsi" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Kota / Kabupaten">
                <Input data-testid="input-city" value={common.city} onChange={(e) => setC("city", e.target.value)} placeholder="Jakarta Selatan" />
              </Field>
              {reporterField}
              <Field label="Jumlah Klaim Sebelumnya">
                <Input data-testid="input-previous-claims" type="number" min="0" value={common.previous_claims_count} onChange={(e) => setC("previous_claims_count", e.target.value)} />
              </Field>
              <div className="flex items-center gap-3 pt-6">
                <Switch data-testid="switch-documents-complete" checked={common.documents_complete} onCheckedChange={(v) => setC("documents_complete", v)} />
                <Label className="text-sm">Dokumen lengkap</Label>
              </div>
            </div>
            {missingDocsSection}
            {chronologyField}
            {filesSection}
            <div className="border-t border-border pt-5">
              <h3 className="text-sm font-semibold mb-4">Data Khusus Asuransi Jiwa</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nama Tertanggung" required>
                  <Input data-testid="input-insured-name" value={jiwa.insured_name} onChange={(e) => setJiwa((s) => ({ ...s, insured_name: e.target.value }))} />
                </Field>
                <Field label="Usia Tertanggung">
                  <Input data-testid="input-insured-age" type="number" min="0" value={jiwa.insured_age} onChange={(e) => setJiwa((s) => ({ ...s, insured_age: e.target.value }))} />
                </Field>
                <Field label="Penyebab Kematian" required>
                  <Select value={jiwa.cause_of_death} onValueChange={(v) => setJiwa((s) => ({ ...s, cause_of_death: v }))}>
                    <SelectTrigger data-testid="select-cause-of-death"><SelectValue placeholder="Pilih penyebab" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sakit">Sakit</SelectItem>
                      <SelectItem value="kecelakaan">Kecelakaan</SelectItem>
                      <SelectItem value="tidak_diketahui">Tidak Diketahui</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Hubungan Ahli Waris">
                  <Input data-testid="input-beneficiary-relation" value={jiwa.beneficiary_relation} onChange={(e) => setJiwa((s) => ({ ...s, beneficiary_relation: e.target.value }))} placeholder="Istri / Anak / ..." />
                </Field>
              </div>
            </div>
            <div className="border-t border-border pt-5 flex justify-end">
              <Button data-testid="submit-claim-button" onClick={submit} disabled={disabled || loading}
                className="bg-slate-900 hover:bg-slate-800 rounded-none h-10 px-8">
                {loading ? <CircleNotch size={18} className="animate-spin" /> : "Simpan & Skor Klaim"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ===================== KENDARAAN ===================== */}
        <TabsContent value="kendaraan" className="mt-4 space-y-4">
          {/* Box 1: Data Polis / Kendaraan */}
          <div className="bg-card border border-border p-6 space-y-5" data-testid="box-policy-vehicle">
            <div className="flex items-center gap-2 border-b border-border pb-3">
              <Car size={18} className="text-slate-700" />
              <h3 className="text-sm font-semibold uppercase tracking-wide">Data Polis / Kendaraan</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {policyNumberField}
              <Field label="Tanggal Mulai Polis" required>
                <DatePickerID testId="input-policy-start-date-knd" value={common.policy_start_date} onChange={(v) => setC("policy_start_date", v)} />
              </Field>
              <Field label="Tanggal Berakhir Polis">
                <DatePickerID testId="input-policy-end-date" value={common.policy_end_date} onChange={(v) => setC("policy_end_date", v)} />
              </Field>
              <Field label="Nama Tertanggung (di polis)" required>
                <Input data-testid="input-kendaraan-insured-name" value={kendaraan.insured_name} onChange={(e) => setK("insured_name", e.target.value)} placeholder="Sesuai nama di polis" />
              </Field>
              <Field label="Merek / Tipe Kendaraan">
                <Input data-testid="input-vehicle-brand" value={kendaraan.vehicle_brand} onChange={(e) => setK("vehicle_brand", e.target.value)} placeholder="Toyota Avanza" />
              </Field>
              <Field label="Nomor Polisi" required>
                <Input data-testid="input-plate-number" value={kendaraan.plate_number} onChange={(e) => setK("plate_number", e.target.value)} placeholder="B 1234 ABC" />
              </Field>
              <Field label="Tahun Kendaraan">
                <Input data-testid="input-vehicle-year" type="number" value={kendaraan.vehicle_year} onChange={(e) => setK("vehicle_year", e.target.value)} placeholder="2022" />
              </Field>
              <Field label="Jenis Asuransi">
                <Select value={kendaraan.coverage_type} onValueChange={(v) => setK("coverage_type", v)}>
                  <SelectTrigger data-testid="select-coverage-type"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_risk">All Risk (Comprehensive)</SelectItem>
                    <SelectItem value="tlo">TLO (Total Loss Only)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Jenis Penggunaan">
                <Select value={kendaraan.usage_type} onValueChange={(v) => setK("usage_type", v)}>
                  <SelectTrigger data-testid="select-usage-type"><SelectValue placeholder="Pilih penggunaan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pribadi_dinas">Pribadi / Dinas</SelectItem>
                    <SelectItem value="komersial_disewakan">Komersial / Disewakan</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Nilai Kendaraan (Rp)" required>
                <Input data-testid="input-vehicle-value" type="number" min="0" value={kendaraan.vehicle_value} onChange={(e) => setK("vehicle_value", e.target.value)} placeholder="200000000" />
              </Field>
            </div>
          </div>

          {/* Box 2: Data Klaim */}
          <div className="bg-card border border-border p-6 space-y-5" data-testid="box-claim-data">
            <div className="flex items-center gap-2 border-b border-border pb-3">
              <Warning size={18} className="text-slate-700" />
              <h3 className="text-sm font-semibold uppercase tracking-wide">Data Klaim</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Tanggal Kejadian" required>
                <DatePickerID testId="input-incident-date-knd" value={common.incident_date} onChange={(v) => setC("incident_date", v)}
                  disabled={{ after: new Date() }} />
              </Field>
              <Field label="Jenis Kejadian">
                <Select value={kendaraan.incident_type} onValueChange={(v) => setK("incident_type", v)}>
                  <SelectTrigger data-testid="select-incident-type"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kecelakaan">Kecelakaan</SelectItem>
                    <SelectItem value="pencurian">Pencurian</SelectItem>
                    <SelectItem value="kebakaran">Kebakaran</SelectItem>
                    <SelectItem value="kehilangan_total">Kehilangan Total</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Jam Kejadian (0-23)">
                <Input data-testid="input-incident-hour" type="number" min="0" max="23" value={kendaraan.incident_hour} onChange={(e) => setK("incident_hour", e.target.value)} placeholder="14" />
              </Field>
              <Field label="Nama Daerah / Kelurahan">
                <Input data-testid="input-district" value={common.district} onChange={(e) => setC("district", e.target.value)} placeholder="Kemang" />
              </Field>
              <Field label="Kota / Kabupaten">
                <Input data-testid="input-city" value={common.city} onChange={(e) => setC("city", e.target.value)} placeholder="Jakarta Selatan" />
              </Field>
              <Field label="Provinsi" required>
                <Select value={common.province} onValueChange={(v) => setC("province", v)}>
                  <SelectTrigger data-testid="select-province"><SelectValue placeholder="Pilih provinsi" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {reporterField}
              <Field label="Perkiraan Nominal Kerusakan (Rp)" required>
                <Input data-testid="input-claim-amount" type="number" min="0" value={common.claim_amount} onChange={(e) => setC("claim_amount", e.target.value)} placeholder="50000000" />
              </Field>
              <div className="space-y-3 pt-1">
                <div className="flex items-center gap-3">
                  <Switch data-testid="switch-police-report" checked={kendaraan.police_report} onCheckedChange={(v) => setK("police_report", v)} />
                  <Label className="text-sm">Ada laporan polisi</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch data-testid="switch-witness-present" checked={kendaraan.witness_present} onCheckedChange={(v) => setK("witness_present", v)} />
                  <Label className="text-sm">Ada saksi</Label>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border/60 pt-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch data-testid="switch-previous-claims" checked={hadPrevious}
                    onCheckedChange={(v) => { setHadPrevious(v); if (!v) setC("previous_claims_count", 0); else if (!Number(common.previous_claims_count)) setC("previous_claims_count", 1); }} />
                  <Label className="text-sm">Pernah klaim sebelumnya</Label>
                </div>
                {hadPrevious && (
                  <Field label="Berapa kali kejadian sebelumnya?" required>
                    <Input data-testid="input-previous-claims" type="number" min="1" value={common.previous_claims_count}
                      onChange={(e) => setC("previous_claims_count", e.target.value)} />
                  </Field>
                )}
              </div>
              <div className="flex items-start gap-3 pt-1">
                <Switch data-testid="switch-documents-complete" checked={common.documents_complete} onCheckedChange={(v) => setC("documents_complete", v)} />
                <Label className="text-sm pt-0.5">Dokumen lengkap</Label>
              </div>
            </div>
            {missingDocsSection}
            {chronologyField}
            {filesSection}
            <div className="border-t border-border pt-5 flex justify-end">
              <Button data-testid="submit-claim-button-knd" onClick={submit} disabled={disabled || loading}
                className="bg-slate-900 hover:bg-slate-800 rounded-none h-10 px-8">
                {loading ? <CircleNotch size={18} className="animate-spin" /> : "Simpan & Skor Klaim"}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
